<template>
  <div class="nav-search-modal-content" :class="{ 'keyboard-visible': isKeyboardOpen, 'is-empty': isEmpty }">
    <section v-if="isAskMacysPromptsEnabled && promptsPosition === 'top'" id="ask-prompts-top"></section>
    <SearchSuggestions v-if="hasSuggestions" ref="suggestionsEl" @select="selectSuggestedSearch($event, false)" />
    <section v-if="isAskMacysPromptsEnabled && promptsPosition === 'between'" id="ask-prompts-between"></section>
    <ProductRecommendations v-if="hasSuggestions && isRecommendationsEnabled" />

    <template v-if="!hasSuggestions">
      <TrendingQueries v-if="isPopularSearchesEnabled" />
      <SearchHistory ref="historyEl" @select="selectSuggestedSearch($event, true)" @cleared="focusSearchInput" />
      <RVI :class="{ 'rvi-section-short': isShortRvi }" @init="isRVI = true" />
    </template>

    <section v-if="isAskMacysPromptsEnabled && promptsPosition === 'bottom'" id="ask-prompts-bottom"></section>
    <SearchTrendingProductCarousel v-if="!hasSuggestions && isTrendingNow" />

    <Teleport v-if="isAskMacysPromptsEnabled" :to="promptsTargetSelector" defer>
      <AskMacysPromts :has-suggestions="hasSuggestions" />
    </Teleport>
  </div>
</template>

<script setup>
import { defineAsyncComponent, computed, ref, watch } from 'vue';
import { GET_IS_DESKTOP_VIEW, GET_IS_HAVE_SUGGESTIONS, GET_IS_MOBILE_VIEW } from '#shared/types/getters';
import { tagSuggestedSearch } from '#shared/features/analytics/searchTags';
import useSearchHistory from '~/composables/dumb/useSearchHistory';
import SearchHistory from './SearchHistory.common';
import TrendingQueries from './TrendingQueries.common';
import SearchSuggestions from './SearchSuggestions.common';
import useExperiments from '~/composables/useExperiments';
import useExperimentsArms from '~/composables/useExperimentsArms';
import useKillswitches from '~/composables/useKillswitches';
import AskMacysPromts from './AskMacysPromts.common';
import { isSSR } from '#shared/utils/isSSR';
import { useAskAiVisibility } from '~/composables/useAskAiVisibility';

const RVI = defineAsyncComponent(() => import('./RVI.common'));
const ProductRecommendations = defineAsyncComponent(() => import('./ProductRecommendations.common'));
const SearchTrendingProductCarousel = defineAsyncComponent(() => import('./SearchTrendingProductCarousel.common'));

defineProps({
  isKeyboardOpen: {
    type: Boolean,
    default: false,
  },
  focusSearchInput: {
    type: Function,
    required: true,
  },
});

const emits = defineEmits(['is-empty']);

const MIN_SEARCH_VALUE_LENGTH = 2;
const { isMcom } = useEnvProps();
const navViewType = useNavViewType();
const navSearchSuggestions = useNavSearchSuggestions();
const experiments = useExperiments();
const experimentsArms = useExperimentsArms();
const killswitches = useKillswitches();
const navHeader = useNavHeader();
const { isAiScriptEnabled } = useAskAiVisibility();

const isAskMacysPromptsEnabled = computed(() => isAiScriptEnabled.value && experiments.value.askMacysPromptsEnabled);
const isSearching = computed(() => navHeader.searchValue.trim().length >= MIN_SEARCH_VALUE_LENGTH);

const { searchHistory, addToSearchHistory } = useSearchHistory();

const suggestionsEl = ref(null);
const historyEl = ref(null);
const isRVI = ref(isSSR() ? false : (localStorage.getItem('recentlyViewedItems') || '').length);

const isMobile = computed(() => navViewType[GET_IS_MOBILE_VIEW]);
const isPopularSearchesEnabled = computed(() => isMobile.value && killswitches.value.masterPopularSearchesEnabled);

const isTrendingNow = computed(() => {
  if (isPopularSearchesEnabled.value) return false;
  /**
   * newSearchModalEnabled experiment scaled for mcom (it is now default behavior),
   * but still needed for bcom to check if trending now carousel should be shown.
   */
  return isMcom ? true : experiments.value.newSearchModalEnabled;
});

const hasSuggestions = computed(() => navSearchSuggestions[GET_IS_HAVE_SUGGESTIONS]);
const suggestions = computed(() => navSearchSuggestions.data);
const isDesktop = computed(() => navViewType[GET_IS_DESKTOP_VIEW]);
const isRecommendationsEnabled = computed(() => isDesktop.value && (isMcom || experiments.value.bestSellerExperimentEnabled));
const isShortRvi = computed(() => isTrendingNow.value || (isMcom && isPopularSearchesEnabled.value));
const promptsPosition = computed(() => {
  if (isMcom) {
    if (experimentsArms.value.askMacysPromptsArmOne) {
      return 'top';
    }
    if (isSearching.value && hasSuggestions.value) {
      return 'between';
    }
    return 'bottom';
  }

  if (isSearching.value && hasSuggestions.value) {
    return 'bottom';
  }

  return experimentsArms.value.askMacysPromptsArmOne ? 'top' : 'bottom';
});
const promptsTargetSelector = computed(() => `#ask-prompts-${promptsPosition.value}`);

const isEmpty = computed(() => {
  if (isMcom) return false;
  if (hasSuggestions.value) return false;
  if (isPopularSearchesEnabled.value) return false;
  if (searchHistory.value.length > 0) return false;
  if (isRVI.value) return false;
  if (isTrendingNow.value) return false;
  if (isAskMacysPromptsEnabled.value) return false;
  return true;
});

watch(
  isEmpty,
  (val) => {
    emits('is-empty', val);
  },
  { immediate: true },
);

const selectSuggestedSearch = ({ item, index }, isRecentSearch) => {
  addToSearchHistory(item);
  const suggestionsLength = suggestions.value.length || searchHistory.value.length;
  tagSuggestedSearch(item, index, suggestionsLength, isRecentSearch);
};

defineExpose({
  suggestionsEl,
  historyEl,
  isEmpty,
});
</script>

<style lang="scss">
@import '~/assets/scss/mixins/index.scss';

.nav-search-modal-content {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  @include breakpoint(medium down) {
    padding-bottom: 48px;
    margin-right: -16px;

    &.keyboard-visible {
      padding-bottom: 350px;
    }

    section:not(.search-modal-carousel) {
      padding-right: 16px;
    }
  }

  section + section {
    @include margin-top-m;
  }
}

.search-modal-carousel {
  h3 {
    @include header(h6);
  }
}

.mew-search-modal-clean {
  .nav-search-modal-content {
    margin-right: 0;
    padding-bottom: 0;
    max-height: calc(100vh - 137px);

    section:not(.search-modal-carousel) {
      padding-right: 0;
    }
  }
}
</style>

<style lang="scss" brand="mcom">
@import '~/assets/scss/mixins/index.scss';

.nav-search-modal-content {
  @include breakpoint(medium down) {
    max-height: calc(100vh - 96px);
  }
}

.mew-search-modal-clean {
  .nav-search-modal-content {
    max-height: calc(100vh - 137px);
  }
}
</style>

<style lang="scss" brand="bcom">
@import '~/assets/scss/mixins/index.scss';

.nav-search-modal-content {
  @include breakpoint(medium down) {
    max-height: calc(100vh - 88px);
  }

  &.is-empty {
    display: none;
  }

  h3,
  h6 {
    text-transform: none;
  }
}

.mew-search-modal-clean {
  .nav-search-modal-content {
    max-height: calc(100vh - 154px);
  }
}
</style>
